import React from "react";

import clsx from "clsx";

import ChangeFractionDisplay from "@components/change-fraction";
import { AnnotationsFlyoutButton } from "@features/custom-annotations";
import { type LegendMetric } from "@pages/sales-v2/components/chart";
import { MetricValue } from "@shared/metric-value";
import Spinner from "@shared/spinner";

import { useReportBodyState } from "../report-body-control";

interface ChartLegendProps {
  legendMetrics: LegendMetric[];
  selectMetric: (index: number) => void;
  selectedMetricIndices: number[];
  customizationModal?: React.ReactElement;
}

export const ChartLegend = ({
  legendMetrics,
  selectMetric,
  selectedMetricIndices,
  customizationModal,
}: ChartLegendProps) => {
  const { dateRange } = useReportBodyState();

  return (
    <div className="py-4">
      <div className="flex justify-between">
        <div data-test="sales-chart-legend-v2" className="flex flex-wrap">
          {legendMetrics.map((metric, index) => {
            const active = selectedMetricIndices.includes(index);
            const {
              currentValue,
              compareValue,
              hideComparisonFromTabDisplay,
              changeFractionAreaText,
              isPositiveChangeGood,
            } = metric;

            const changeFraction = (currentValue - compareValue) / compareValue;
            const positiveChange = changeFraction >= 0;
            const delta = hideComparisonFromTabDisplay
              ? undefined
              : {
                  changeFraction,
                  isPositiveChangeGood,
                };

            const hasChange =
              typeof changeFraction !== "undefined" &&
              changeFraction !== null &&
              !isNaN(changeFraction) &&
              delta;

            return (
              <button
                key={metric.name}
                className={clsx(
                  active && "border-2 border-primary",
                  "bg-white rounded-lg border border-gray",
                  "mb-2 mr-2 py-2 px-3 text-sm text-left",
                )}
                onClick={() => selectMetric(index)}
              >
                {metric.loading ? (
                  <div className="flex-center">
                    <Spinner />
                  </div>
                ) : (
                  <div>
                    <div className="font-semibold text-sm mb-0.5">
                      {metric.name}
                    </div>
                    <div className="text-xs flex items-center space-x-1 font-mono">
                      <MetricValue
                        value={currentValue}
                        precision="exact"
                        as="span"
                        format={metric.format}
                      />
                      <span>
                        {hasChange && (
                          <i
                            className={clsx(
                              "fa-regular fa-sharp",
                              positiveChange
                                ? {
                                    "fa-arrow-trend-up text-good":
                                      isPositiveChangeGood,
                                    "fa-arrow-trend-down text-bad":
                                      !isPositiveChangeGood,
                                  }
                                : {
                                    "fa-arrow-trend-up text-good":
                                      !isPositiveChangeGood,
                                    "fa-arrow-trend-down text-bad":
                                      isPositiveChangeGood,
                                  },
                            )}
                          />
                        )}
                      </span>
                      <div>
                        {changeFractionAreaText ? (
                          <small className="text-muted">
                            {changeFractionAreaText}
                          </small>
                        ) : delta ? (
                          <ChangeFractionDisplay {...delta} />
                        ) : (
                          <small>&nbsp; </small>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </button>
            );
          })}
        </div>
        <div className="flex items-center space-x-2">
          {customizationModal}
          <AnnotationsFlyoutButton
            dateRange={dateRange}
            buttonClass="font-normal"
          />
        </div>
      </div>
    </div>
  );
};
